// src/lib/capture/createMobileSource.ts — CBAM mobile capture (Phase 1)
//
// Thin client wrapper over the research_create_mobile_source RPC. Registers a
// new mobile capture as a research_source (and, when a consent envelope is
// supplied, its consent row) and returns the new source's uuid.

import { supabase } from "@/integrations/supabase/client";
import type { Consent } from "./types";

export interface CreateMobileSourceOpts {
  /** Stable client-generated uuid for this capture (idempotency key). */
  captureUuid: string;
  /** research_sources.source_type, e.g. "manual_audio". Defaults server-side. */
  sourceType?: string;
  /** Human-facing title for the source. */
  title?: string;
  /** Arbitrary capture metadata (device, duration, geo, etc.). */
  rawMetadata?: Record<string, unknown>;
  /** Consent envelope for third-party-audio captures. */
  consent?: Consent;
  /** research_events.id to tag this capture to (conference/sub-event). Null/undefined → no event. */
  eventId?: string | null;
}

export interface CreateMobileSourceResult {
  /** uuid of the newly created research_sources row. */
  sourceId: string;
}

/**
 * Create a mobile capture source. Throws if the RPC returns an error.
 */
export async function createMobileSource(
  opts: CreateMobileSourceOpts,
): Promise<CreateMobileSourceResult> {
  const { captureUuid, sourceType, title, rawMetadata, consent, eventId } = opts;

  // Cast rpc name: research_create_mobile_source is not in the generated types yet.
  const { data, error } = await (supabase.rpc as any)(
    "research_create_mobile_source",
    {
      _capture_uuid: captureUuid,
      _source_type: sourceType ?? null,
      _title: title ?? null,
      _raw_metadata: rawMetadata ?? null,
      _basis: consent?.basis ?? null,
      _parties: consent?.parties ?? null,
      _jurisdiction: consent?.jurisdiction ?? null,
      _disclosure_version: consent?.disclosureVersion ?? null,
      _verbal_segment_present: consent?.verbalSegmentPresent ?? null,
      _emotion_inference_ok: consent?.emotionInferenceOk ?? null,
      // 11th arg (optional): tag this source to a research_event when one is selected.
      _event_id: eventId ?? null,
    },
  );

  if (error) {
    throw new Error(`createMobileSource failed: ${error.message}`);
  }

  // The RPC returns the new source uuid directly.
  return { sourceId: data as string };
}
